import { Link, useNavigate } from "react-router-dom";
import { format, isToday } from "date-fns";
import { nn } from "date-fns/locale";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/admin/PageHeader";
import { CalBar } from "@/components/admin/calendar/Bar";
import { isoWeek, onDay, sortByStart, WHOLE } from "@/components/admin/calendar/engine";
import { fmtRange, toISODate, WEEKDAYS_SHORT } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Seven columns for the current week – hairlines between the days, today marked with a sol pill. */
export const WeekStrip = ({ days, items, colours, loading, weekCount }) => {
  const navigate = useNavigate();
  const today = new Date();
  return (
    <section data-testid="dashboard-week">
      <SectionHeading
        title="Denne veka"
        description={`Veke ${isoWeek(today)} · ${fmtRange(toISODate(days[0]), toISODate(days[6]))}${weekCount ? ` · ${weekCount} ${weekCount === 1 ? "oppføring" : "oppføringar"}` : ""}`}
        action={<Link to="/admin/kalender?view=week" className="arrow-nudge-x inline-flex items-center gap-1 text-[13px] font-medium u-link" data-testid="dashboard-calendar-link">Opne kalenderen <ArrowRight className="h-3.5 w-3.5" /></Link>}
      />
      {loading ? <Skeleton className="h-44 rounded-2xl" /> : (
        <div className="admin-card grid grid-cols-7 overflow-hidden divide-x divide-hairline" data-testid="dashboard-week-grid">
          {days.map((day, i) => {
            const iso = toISODate(day);
            const list = sortByStart(items.filter((it) => onDay(it, iso)));
            const tod = isToday(day);
            const go = () => navigate(`/admin/kalender?view=week&d=${iso}`);
            return (
              <div key={iso} className={cn("min-w-0 px-1.5 pb-3 pt-2.5", tod && "cal-today")} data-testid={`dashboard-day-${iso}`}>
                <button type="button" onClick={go} className="flex w-full flex-col items-center gap-0.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-secondary/70 sm:flex-row sm:items-baseline sm:justify-start sm:gap-1.5" aria-label={format(day, "EEEE d. MMMM", { locale: nn })}>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:text-[10.5px]"><span className="sm:hidden">{WEEKDAYS_SHORT[i].slice(0, 1)}</span><span className="hidden sm:inline">{WEEKDAYS_SHORT[i]}</span></span>
                  <span className={cn("flex h-6 min-w-6 items-center justify-center rounded-full px-1 font-display text-[14px] font-semibold tabular", tod && "bg-sol text-ink")}>{format(day, "d")}</span>
                </button>
                <div className="mt-1.5 space-y-1">
                  {list.length === 0 ? <p className="hidden px-1 pt-1 text-[11.5px] text-muted-foreground/60 sm:block">–</p> : list.slice(0, 4).map((it) => (
                    <CalBar key={it.key} item={it} colours={colours} dense showTime={false} onOpen={go} className="hidden sm:flex" />
                  ))}
                  {list.length > 0 && <div className="flex flex-wrap justify-center gap-[3px] sm:hidden">{list.slice(0, 6).map((it) => <span key={it.key} className="h-1.5 w-1.5 rounded-full" style={{ background: it.kind === "pending" ? "hsl(var(--status-pending))" : `hsl(var(--room-${it.room_ids?.includes(WHOLE) ? 8 : colours[it.room_ids?.[0]] || 7}))` }} />)}</div>}
                  {list.length > 4 && <button type="button" onClick={go} className="hidden px-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground sm:block">+{list.length - 4} meir</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
